import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Get, Patch } from "@repo/utils/api";
import UFile from "@repo/utils-client/UFile";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Staff from "@repo/vygor-models/Staff";

import { api } from "..";

import { Organization, useOrganizations } from "./organizations";

export { Staff };

export type StaffsRest = {
  [id: Staff.Id]: Patch<Staff.Params.Self, { staff: Staff }>;
};

export type InvitationsRest = Get<
  {},
  { invitations: Staff.Invitation[]; organizations: Organization[] }
> & {
  [id: Staff.Invitation.Id]: Patch<
    Staff.Invitation.Params.Self,
    {
      invitation: Staff.Invitation;
      staffProfile?: Staff;
      organization?: Organization;
    }
  >;
};

const initialState = {
  byId: {} as Record<Staff.Id, Staff>,
  ownStaffByOrgId: {} as Record<Organization.Id, Staff.Id>,
  invitationById: {} as Record<Staff.Invitation.Id, Staff.Invitation>,
  ownInvitations: null as Staff.Invitation.Id[] | null,
};

export const { Context, Provider, useStaffs } = FeatureStore.init(
  "Staffs",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const [, setOrganizations] = useOrganizations();

    const accessors = {
      byId: (id: Staff.Id | undefined) => id && store.byId[id],
      ownForOrg: (id: Organization.Id | undefined) =>
        id && accessors.byId(store.ownStaffByOrgId[id]),
      invitation: (id: Staff.Invitation.Id | undefined) =>
        id && store.invitationById[id],
      ownInvitations: attachAction(
        () =>
          store.ownInvitations
            ?.map((id) => store.invitationById[id])
            .filter((invitation) => !!invitation),
        () => actions.fetchInvitations(),
      ),
    };

    const mutations = {
      one(staff: Staff) {
        setStore("byId", staff.id, reconcile(staff));
      },
      many(staffs: Staff[]) {
        batch(() => {
          for (const staff of staffs) {
            mutations.one(staff);
          }
        });
      },
      ownForOrg(id: Organization.Id, staff: Staff) {
        batch(() => {
          setStore("ownStaffByOrgId", id, staff.id);
          mutations.one(staff);
        });
      },
      remove(id: Staff.Id) {
        setStore("byId", ({ [id]: _, ...rest }) => rest);
      },
      invitations: {
        one(invitation: Staff.Invitation) {
          setStore("invitationById", invitation.id, invitation);
        },
        many(invitations: Staff.Invitation[]) {
          batch(() => {
            for (const invitation of invitations) {
              mutations.invitations.one(invitation);
            }
          });
        },
      },
    };

    const actions = withLogger("action", {
      ...mutations,
      async uploadPfp(id: Staff.Id, pfp: File) {
        return api.staffs[id]?.PATCH({
          pfpBase64: await UFile.toBase64(pfp),
        });
      },
      fetchInvitations() {
        return api.invitations
          .GET({})
          .then(({ invitations, organizations }) => {
            batch(() => {
              mutations.invitations.many(invitations);
              setStore(
                "ownInvitations",
                invitations.map(({ id }) => id),
              );
              setOrganizations.many(organizations);
            });
          });
      },
      updateInvitationStatus(
        id: Staff.Invitation.Id,
        status: Exclude<Staff.Invitation.Status, "pending">,
      ) {
        return api.invitations[id]!.PATCH({ status }).then(
          ({ invitation, staffProfile, organization }) => {
            batch(() => {
              mutations.invitations.one(invitation);
              if (staffProfile) mutations.one(staffProfile);
              if (organization) {
                setOrganizations.one(organization);
                setOrganizations.addOwnStaff(organization.id);
              }
            });
          },
        );
      },
    });

    return { accessors, actions };
  },
);
